import React from 'react';
import { Alert, Box, Button, Checkbox, Chip, Stack, Tooltip, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';
import {
    CAPABILITIES,
    CAPABILITY_GROUPS,
    CAPABILITY_REQUIREMENTS,
    MANAGE_IMPLIES_VIEW,
    ROLE_DEFAULTS,
    arraysMatchAsSet,
    getAssignableTemplates,
    getEffectiveCapabilities,
    getMissingPrerequisites,
} from '../../../helpers/capabilities';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

export default function UserCapabilitiesSection({
    capabilities,
    setCapabilities,
    role,
    setRole,
    allowedTemplates,
    isEditingSelf,
    currentUserIsSuperAdmin,
    currentUserCapabilities,
}) {
    const { t } = useTranslation();
    const capsSet = new Set(capabilities);

    // Prevent users from removing their own users.manage capability
    const lockedSet = new Set(isEditingSelf ? [CAPABILITIES.USERS_MANAGE] : []);

    const defaultCaps = ROLE_DEFAULTS[role] || [];
    const defaultSet = new Set(defaultCaps);

    const visibleTemplates =
        allowedTemplates ||
        getAssignableTemplates(currentUserCapabilities, currentUserIsSuperAdmin);

    // Admins (venues.manage + shows.manage) can customize individual checkboxes.
    // Managers and below can only pick templates.
    const actorEffective = currentUserIsSuperAdmin
        ? null
        : getEffectiveCapabilities(currentUserCapabilities);
    const canCustomize =
        currentUserIsSuperAdmin ||
        (actorEffective &&
            actorEffective.has(CAPABILITIES.VENUES_MANAGE) &&
            actorEffective.has(CAPABILITIES.SHOWS_MANAGE));
    const isTemplateOnlyMode = !canCustomize;
    // In template-only mode, only show capabilities that belong to assignable templates.
    // When the actor can customize, show all capabilities (locked ones will be individually disabled).
    const visibleCapabilitySet = isTemplateOnlyMode
        ? new Set(visibleTemplates.flatMap((templateRole) => ROLE_DEFAULTS[templateRole] || []))
        : null;

    const activeTemplate =
        visibleTemplates.find((templateRole) => {
            const templateCaps = ROLE_DEFAULTS[templateRole];
            return templateCaps && arraysMatchAsSet(capabilities, templateCaps);
        }) ?? null;

    const handleTemplateClick = (templateRole) => {
        const templateCaps = ROLE_DEFAULTS[templateRole];
        if (templateCaps) {
            setCapabilities([...templateCaps]);
            if (setRole) {
                setRole(templateRole);
            }
        }
    };

    const VIEW_IMPLIED_BY = Object.fromEntries(
        Object.entries(MANAGE_IMPLIES_VIEW).map(([m, v]) => [v, m]),
    );

    // A view cap is implied (locked) when its manage counterpart is active
    const impliedSet = new Set();
    for (const [manage, view] of Object.entries(MANAGE_IMPLIES_VIEW)) {
        if (capsSet.has(manage)) impliedSet.add(view);
    }

    const handleToggle = (capKey) => {
        let next;
        if (capsSet.has(capKey)) {
            // Turning OFF
            next = capabilities.filter((c) => c !== capKey);
            // If turning off a view cap, also remove its manage counterpart
            const manageCap = VIEW_IMPLIED_BY[capKey];
            if (manageCap) {
                next = next.filter((c) => c !== manageCap);
            }
            // Drop any cap that required the one being turned off (otherwise
            // the user is left with a non-functional dependent permission).
            for (const [cap, deps] of Object.entries(CAPABILITY_REQUIREMENTS)) {
                if (deps.includes(capKey) && next.includes(cap)) {
                    next = next.filter((c) => c !== cap);
                }
            }
        } else {
            // Turning ON
            next = [...capabilities, capKey];
            // Pull in every prerequisite, not just the sibling view cap, so
            // e.g. seating.cross_ticket_type also brings seating.modify with it.
            const queue = [capKey];
            const seen = new Set(next);
            while (queue.length > 0) {
                const current = queue.shift();
                const deps = CAPABILITY_REQUIREMENTS[current] || [];
                for (const dep of deps) {
                    if (!seen.has(dep)) {
                        seen.add(dep);
                        next.push(dep);
                        queue.push(dep);
                    }
                }
            }
        }
        setCapabilities(next);
    };

    const handleResetToDefaults = () => {
        setCapabilities([...defaultCaps]);
    };

    const isCustomized =
        capabilities.length !== defaultCaps.length || capabilities.some((c) => !defaultSet.has(c));

    const isSuperAdmin = role === 'superadmin';

    if (isSuperAdmin) {
        return (
            <Alert severity="info" sx={{ textAlign: 'left' }}>
                {t('capabilities.superadminBypass')}
            </Alert>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 0.5,
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'left' }}>
                    {t('capabilities.sectionTitle')}
                </Typography>
                {isCustomized && (
                    <Button
                        variant="outlined"
                        color="warning"
                        size="small"
                        onClick={handleResetToDefaults}
                    >
                        {t('capabilities.resetToDefaults')}
                    </Button>
                )}
            </Box>

            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', textAlign: 'left', mb: 1 }}
            >
                {t('capabilities.templateDescription')}
            </Typography>

            <Box sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {visibleTemplates.map((templateRole) => {
                        const isActive = activeTemplate === templateRole;
                        return (
                            <Chip
                                key={templateRole}
                                label={t(`users.roleLabels.${templateRole}`)}
                                onClick={() => handleTemplateClick(templateRole)}
                                variant={isActive ? 'filled' : 'outlined'}
                                color={isActive ? 'primary' : 'default'}
                                size="small"
                                sx={{ fontWeight: isActive ? 600 : 400 }}
                            />
                        );
                    })}
                    {!activeTemplate && canCustomize && (
                        <Chip
                            label={t('capabilities.custom')}
                            variant="filled"
                            color="warning"
                            size="small"
                            sx={{ fontWeight: 600 }}
                        />
                    )}
                </Stack>
            </Box>

            {CAPABILITY_GROUPS.map((group) => {
                let visibleCaps = visibleCapabilitySet
                    ? group.capabilities.filter((cap) => visibleCapabilitySet.has(cap.key))
                    : group.capabilities;
                if (!currentUserIsSuperAdmin && actorEffective) {
                    visibleCaps = visibleCaps.filter((cap) => actorEffective.has(cap.key));
                }
                if (visibleCaps.length === 0) return null;
                return (
                    <Box
                        key={group.labelKey}
                        sx={{
                            display: 'grid',
                            // Stack label above the checkboxes on mobile; two columns from sm up.
                            gridTemplateColumns: { xs: '1fr', sm: '140px 1fr' },
                            alignItems: { xs: 'start', sm: 'center' },
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            py: 0.5,
                        }}
                    >
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                textAlign: 'left',
                            }}
                        >
                            {t(group.labelKey)}
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))',
                                gap: '4px 8px',
                            }}
                        >
                            {visibleCaps.map((cap) => {
                                const actorLacksCapability =
                                    !currentUserIsSuperAdmin &&
                                    actorEffective &&
                                    !actorEffective.has(cap.key);
                                if (actorLacksCapability) return null;
                                const enabled = capsSet.has(cap.key);
                                const isLocked =
                                    isTemplateOnlyMode ||
                                    lockedSet.has(cap.key) ||
                                    impliedSet.has(cap.key);
                                const isExtra = enabled && !defaultSet.has(cap.key);
                                const missingDeps = enabled
                                    ? getMissingPrerequisites(cap.key, capsSet)
                                    : [];
                                const hasMissingDeps = missingDeps.length > 0;
                                const tooltipBase = t(`${cap.capKey}.description`);
                                const tooltipTitle = hasMissingDeps
                                    ? `${tooltipBase} — ${t('capabilities.missingDependency', {
                                          deps: missingDeps.join(', '),
                                      })}`
                                    : tooltipBase;
                                return (
                                    <Tooltip
                                        key={cap.key}
                                        title={tooltipTitle}
                                        placement="top"
                                        arrow
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                cursor: isLocked ? 'default' : 'pointer',
                                            }}
                                            onClick={() => !isLocked && handleToggle(cap.key)}
                                        >
                                            <Checkbox
                                                checked={enabled}
                                                disabled={isLocked}
                                                size="small"
                                                tabIndex={-1}
                                                sx={{
                                                    p: 0.25,
                                                    ...(isExtra && {
                                                        color: 'warning.main',
                                                        '&.Mui-checked': { color: 'warning.main' },
                                                    }),
                                                    ...(isLocked && { opacity: 0.5 }),
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    ml: 0.25,
                                                    textAlign: 'left',
                                                    ...(isExtra && { color: 'warning.dark' }),
                                                }}
                                            >
                                                {t(`${cap.capKey}.label`)}
                                            </Typography>
                                            {isLocked && (
                                                <LockIcon
                                                    sx={{
                                                        fontSize: 11,
                                                        color: 'text.disabled',
                                                        ml: 0.25,
                                                    }}
                                                />
                                            )}
                                            {hasMissingDeps && (
                                                <WarningAmberIcon
                                                    sx={{
                                                        fontSize: 13,
                                                        color: 'warning.main',
                                                        ml: 0.25,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}
